import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "leaderboard-content" }
const _hoisted_2 = { class: "d-flex align-center mb-6" }
const _hoisted_3 = { class: "text-h5 font-weight-bold ml-2" }
const _hoisted_4 = {
  key: 0,
  class: "d-flex align-center ml-4"
}
const _hoisted_5 = {
  key: 1,
  class: "d-flex align-center ml-4"
}
const _hoisted_6 = {
  key: 0,
  class: "d-flex justify-center align-center py-8"
}
const _hoisted_7 = { key: 2 }
const _hoisted_8 = {
  key: 0,
  class: "d-flex flex-column align-center justify-center py-12"
}
const _hoisted_9 = { class: "text-body-2 text-grey" }

import { ref, onMounted, computed } from 'vue'
import MainLayout from './components/MainLayout.vue'
import LeaderboardTable from './components/LeaderboardTable.vue'

interface LeaderboardEntry {
  rank: number
  name: string
  points: number
  slackId: string
}

interface LeaderboardState {
  [key: string]: LeaderboardEntry[]
}

interface LoadingState {
  [key: string]: boolean
}

interface ErrorState {
  [key: string]: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Leaderboard',
  setup(__props) {

const periodOptions = [
  { label: 'Monthly', value: 'monthly' },
  { label: 'Yearly', value: 'yearly' },
  { label: 'All Time', value: 'allTime' }
]
const selectedPeriod = ref('monthly')

const now = new Date()
const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'
] as const

// Track selected month/year for monthly view
const selectedMonth = ref(now.getMonth())
const selectedYear = ref(now.getFullYear())

// Track selected year for yearly view
const selectedYearly = ref(now.getFullYear())

const currentMonth = monthNames[now.getMonth()]
const currentYear = now.getFullYear()

const leaderboardTitle = computed(() => {
  if (selectedPeriod.value === 'monthly') {
    const monthName = monthNames[selectedMonth.value]
    return `Monthly Leaders – ${monthName} ${selectedYear.value}`
  } else if (selectedPeriod.value === 'yearly') {
    return `Yearly Leaders – ${selectedYearly.value}`
  } else {
    return 'All Time Leaders'
  }
})

const canGoPreviousMonth = computed(() => {
  if (selectedPeriod.value !== 'monthly') return false
  return selectedYear.value > 2020 || (selectedYear.value === 2020 && selectedMonth.value > 0)
})

const canGoNextMonth = computed(() => {
  if (selectedPeriod.value !== 'monthly') return false
  const currentDate = new Date()
  return selectedYear.value < currentDate.getFullYear() || 
         (selectedYear.value === currentDate.getFullYear() && selectedMonth.value < currentDate.getMonth())
})

const canGoPreviousYear = computed(() => {
  if (selectedPeriod.value !== 'yearly') return false
  return selectedYearly.value > 2020
})

const canGoNextYear = computed(() => {
  if (selectedPeriod.value !== 'yearly') return false
  const currentDate = new Date()
  return selectedYearly.value < currentDate.getFullYear()
})

async function goToPreviousMonth() {
  if (selectedMonth.value === 0) {
    selectedMonth.value = 11
    selectedYear.value--
  } else {
    selectedMonth.value--
  }
  try {
    loading.value.monthly = true
    error.value.monthly = ''
    leaderboards.value.monthly = await fetchLeaderboard('monthly')
  } catch (e) {
    error.value.monthly = 'Failed to load leaderboard'
  } finally {
    loading.value.monthly = false
  }
}

async function goToNextMonth() {
  if (selectedMonth.value === 11) {
    selectedMonth.value = 0
    selectedYear.value++
  } else {
    selectedMonth.value++
  }
  try {
    loading.value.monthly = true
    error.value.monthly = ''
    leaderboards.value.monthly = await fetchLeaderboard('monthly')
  } catch (e) {
    error.value.monthly = 'Failed to load leaderboard'
  } finally {
    loading.value.monthly = false
  }
}

async function goToCurrentMonth() {
  selectedMonth.value = now.getMonth()
  selectedYear.value = now.getFullYear()
  try {
    loading.value.monthly = true
    error.value.monthly = ''
    leaderboards.value.monthly = await fetchLeaderboard('monthly')
  } catch (e) {
    error.value.monthly = 'Failed to load leaderboard'
  } finally {
    loading.value.monthly = false
  }
}

async function goToPreviousYear() {
  selectedYearly.value--
  try {
    loading.value.yearly = true
    error.value.yearly = ''
    leaderboards.value.yearly = await fetchLeaderboard('yearly')
  } catch (e) {
    error.value.yearly = 'Failed to load leaderboard'
  } finally {
    loading.value.yearly = false
  }
}

async function goToNextYear() {
  selectedYearly.value++
  try {
    loading.value.yearly = true
    error.value.yearly = ''
    leaderboards.value.yearly = await fetchLeaderboard('yearly')
  } catch (e) {
    error.value.yearly = 'Failed to load leaderboard'
  } finally {
    loading.value.yearly = false
  }
}

async function goToCurrentYear() {
  selectedYearly.value = now.getFullYear()
  try {
    loading.value.yearly = true
    error.value.yearly = ''
    leaderboards.value.yearly = await fetchLeaderboard('yearly')
  } catch (e) {
    error.value.yearly = 'Failed to load leaderboard'
  } finally {
    loading.value.yearly = false
  }
}

const leaderboards = ref<LeaderboardState>({
  monthly: [],
  yearly: [],
  allTime: []
})

const loading = ref<LoadingState>({
  monthly: false,
  yearly: false,
  allTime: false
})

const error = ref<ErrorState>({
  monthly: '',
  yearly: '',
  allTime: ''
})

async function fetchLeaderboard(period: string): Promise<LeaderboardEntry[]> {
  const apiPeriod = period === 'allTime' ? 'all-time' : period
  let url = `${process.env.VUE_APP_API_BASE_URL}/leaderboard?period=${apiPeriod}`
  
  // Add month/year parameters for monthly view
  if (period === 'monthly') {
    url += `&month=${selectedMonth.value + 1}&year=${selectedYear.value}`
  }
  
  // Add year parameter for yearly view
  if (period === 'yearly') {
    url += `&year=${selectedYearly.value}`
  }
  
  const response = await fetch(url, { credentials: 'include' })
  if (!response.ok) {
    throw new Error('Failed to fetch leaderboard')
  }
  return await response.json()
}

async function fetchAllLeaderboards() {
  console.log('Starting fetchAllLeaderboards...')
  // Workspace emoji is already loaded at app level for authenticated users
  
  for (const period of ['monthly', 'yearly', 'allTime']) {
    try {
      loading.value[period] = true
      error.value[period] = ''
      leaderboards.value[period] = await fetchLeaderboard(period)
    } catch (e) {
      error.value[period] = 'Failed to load leaderboard'
    } finally {
      loading.value[period] = false
    }
  }
}

onMounted(fetchAllLeaderboards)

return (_ctx: any,_cache: any) => {
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!

  return (_openBlock(), _createBlock(MainLayout, null, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_v_select, {
            modelValue: selectedPeriod.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedPeriod).value = $event)),
            items: periodOptions,
            "item-title": "label",
            "item-value": "value",
            label: "Leaderboard Period",
            variant: "outlined",
            density: "comfortable",
            class: "mr-4",
            style: {"max-width":"220px"}
          }, null, 8, ["modelValue"]),
          _createElementVNode("span", _hoisted_3, _toDisplayString(leaderboardTitle.value), 1),
          (selectedPeriod.value === 'monthly')
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createVNode(_component_v_btn, {
                  icon: "mdi-chevron-left",
                  variant: "text",
                  size: "small",
                  disabled: !canGoPreviousMonth.value,
                  onClick: goToPreviousMonth,
                  class: "mr-2"
                }, null, 8, ["disabled"]),
                _createVNode(_component_v_btn, {
                  variant: "outlined",
                  size: "small",
                  onClick: goToCurrentMonth,
                  class: "mr-2"
                }, {
                  default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                    _createTextVNode(" Current ", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  icon: "mdi-chevron-right",
                  variant: "text",
                  size: "small",
                  disabled: !canGoNextMonth.value,
                  onClick: goToNextMonth
                }, null, 8, ["disabled"])
              ]))
            : _createCommentVNode("", true),
          (selectedPeriod.value === 'yearly')
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createVNode(_component_v_btn, {
                  icon: "mdi-chevron-left",
                  variant: "text",
                  size: "small",
                  disabled: !canGoPreviousYear.value,
                  onClick: goToPreviousYear,
                  class: "mr-2"
                }, null, 8, ["disabled"]),
                _createVNode(_component_v_btn, {
                  variant: "outlined",
                  size: "small",
                  onClick: goToCurrentYear,
                  class: "mr-2"
                }, {
                  default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                    _createTextVNode(" Current ", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  icon: "mdi-chevron-right",
                  variant: "text",
                  size: "small",
                  disabled: !canGoNextYear.value,
                  onClick: goToNextYear
                }, null, 8, ["disabled"])
              ]))
            : _createCommentVNode("", true)
        ]),
        _createVNode(_component_v_card, { elevation: "2" }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => [
                (loading.value[selectedPeriod.value])
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                      _createVNode(_component_v_progress_circular, {
                        indeterminate: "",
                        color: "primary"
                      })
                    ]))
                  : (error.value[selectedPeriod.value])
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 1,
                        type: "error",
                        class: "my-4"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(error.value[selectedPeriod.value]), 1)
                        ]),
                        _: 1
                      }))
                    : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                        (leaderboards.value[selectedPeriod.value].length === 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                              _createVNode(_component_v_icon, {
                                size: "48",
                                color: "grey-lighten-1",
                                class: "mb-4"
                              }, {
                                default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                                  _createTextVNode("mdi-trophy-outline", -1)
                                ]))]),
                                _: 1
                              }),
                              _cache[4] || (_cache[4] = _createElementVNode("div", { class: "text-h6 text-medium-emphasis mb-2" }, "No data available", -1)),
                              _createElementVNode("div", _hoisted_9, _toDisplayString(selectedPeriod.value === 'monthly' ? `No leaderboard data for ${monthNames[selectedMonth.value]} ${selectedYear.value}` : 'No leaderboard data available'), 1)
                            ]))
                          : (_openBlock(), _createBlock(LeaderboardTable, {
                              key: 1,
                              entries: leaderboards.value[selectedPeriod.value]
                            }, null, 8, ["entries"]))
                      ]))
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ])
    ]),
    _: 1
  }))
}
}

})